import React from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuthStore, useThemeStore, useUIStore, useFeaturesStore } from '../../stores';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import {
  LayoutDashboard,
  Package,
  Users,
  FileText,
  Calendar,
  CalendarDays,
  Receipt,
  Settings,
  Menu,
  LogOut,
  Moon,
  Sun,
  Search,
  X,
  Zap,
  UsersRound,
  Building2,
  FileCheck,
  Target,
} from 'lucide-react';

const BASE_NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Tableau de bord' },
  { to: '/products', icon: Package, label: 'Produits' },
  { to: '/clients', icon: Users, label: 'Clients' },
  { to: '/quotes', icon: FileText, label: 'Devis' },
  { to: '/invoices', icon: FileCheck, label: 'Factures' },
  { to: '/calendar', icon: CalendarDays, label: 'Planning' },
  { to: '/events', icon: Calendar, label: 'Événements' },
  { to: '/expenses', icon: Receipt, label: 'Dépenses' },
  { to: '/team', icon: UsersRound, label: 'Équipe' },
  { to: '/settings/company', icon: Building2, label: 'Entreprise' },
  { to: '/settings', icon: Settings, label: 'Paramètres' },
];

const CRM_NAV_ITEM = { to: '/leads', icon: Target, label: 'Prospection', feature: 'crm' };

const BOTTOM_NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Accueil' },
  { to: '/quotes', icon: FileText, label: 'Devis' },
  { to: '/calendar', icon: CalendarDays, label: 'Planning' },
  { to: '/expenses', icon: Receipt, label: 'Scanner' },
];

const NavItem = ({ to, icon: Icon, label, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 touch-target',
        isActive
          ? 'bg-primary text-primary-foreground font-medium'
          : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
      )}
      data-testid={`nav-${to.replace('/', '') || 'dashboard'}`}
    >
      <Icon className="w-5 h-5 shrink-0" />
      <span className="text-sm">{label}</span>
    </NavLink>
  );
};

const Sidebar = ({ onClose }) => {
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const crmEnabled = useFeaturesStore((s) => s.crm);

  const navItems = React.useMemo(() => {
    const items = [...BASE_NAV_ITEMS];
    if (crmEnabled) {
      // Insert CRM after Clients
      const clientsIdx = items.findIndex(i => i.to === '/clients');
      items.splice(clientsIdx + 1, 0, CRM_NAV_ITEM);
    }
    return items;
  }, [crmEnabled]);

  return (
    <div className="flex flex-col h-full bg-card border-r border-border">
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-border">
        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
          <Zap className="w-5 h-5 text-primary-foreground" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-lg">EventPro</h1>
          <p className="text-xs text-muted-foreground">ERP Événementiel</p>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.map((item) => (
            <NavItem key={item.to} {...item} onClick={onClose} />
          ))}
        </nav>
      </ScrollArea>

      {/* User & Theme */}
      <div className="p-3 border-t border-border space-y-2">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground"
          onClick={toggleTheme}
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          <span className="text-sm">{theme === 'dark' ? 'Mode clair' : 'Mode sombre'}</span>
        </Button>

        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-sm font-medium text-primary">
              {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.full_name || 'Utilisateur'}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="shrink-0"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const BottomNav = () => {
  const location = useLocation();

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-card/95 backdrop-blur-xl border-t border-border safe-area-pb"
      data-testid="bottom-nav"
    >
      <div className="flex items-center justify-around px-2 py-1">
        {BOTTOM_NAV_ITEMS.map(({ to, icon: Icon, label }) => {
          const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
          return (
            <NavLink
              key={to}
              to={to}
              className={cn(
                'flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg transition-all min-w-[64px]',
                isActive ? 'text-primary' : 'text-muted-foreground'
              )}
              data-testid={`bottom-nav-${to.replace('/', '') || 'home'}`}
            >
              <Icon className={cn('w-5 h-5', isActive && 'scale-110')} />
              <span className="text-[10px] font-medium">{label}</span>
            </NavLink>
          );
        })}
        <NavLink
          to="/settings"
          className={cn(
            'flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg transition-all min-w-[64px]',
            location.pathname === '/settings' ? 'text-primary' : 'text-muted-foreground'
          )}
          data-testid="bottom-nav-settings"
        >
          <Menu className="w-5 h-5" />
          <span className="text-[10px] font-medium">Menu</span>
        </NavLink>
      </div>
    </nav>
  );
};

const AppLayout = () => {
  const { sidebarOpen, setSidebarOpen } = useUIStore();
  const { theme, toggleTheme } = useThemeStore();

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:block fixed inset-y-0 left-0 z-50 w-64">
        <Sidebar />
      </aside>

      {/* Mobile Sidebar Sheet */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-72 p-0">
          <Sidebar onClose={() => setSidebarOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <main className="md:pl-64 pb-20 md:pb-0 min-h-screen">
        {/* Mobile Header */}
        <header className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 bg-background/95 backdrop-blur-xl border-b border-border">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold">EventPro</span>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            data-testid="mobile-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </header>

        {/* Page Content */}
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Bottom Navigation (Mobile) */}
      <BottomNav />
    </div>
  );
};

export default AppLayout;
